import ApiClient from '../../../Services/ApiClient';
import {
	usePatientBasicInfoQuery,
	useQuestionnaireDetailQuery,
} from '../../../graphql/graphql';
import { sanitizeFileName } from '../Utils/sanitize-file-name';
import useNotification from '../../../Hooks/useNotification.hook';
import useTranslation from '../../../Hooks/useTranslation';
import useApiClient from '../../Base/Hooks/useApiClient';
import usePatientMeta from '../../../Components/PatientProfile/Hooks/usePatientMeta';
import moment from 'moment';

interface ExportQuestionnaire {
	questionnaireCode: string;
	patientId: string;
	afterExport: () => void;
}

const useExportQuestionnaire = ({
	questionnaireCode,
	patientId,
	afterExport,
}: ExportQuestionnaire) => {
	const { displayError } = useApiClient();
	const { showNotificationAlert } = useNotification();
	const { t } = useTranslation();
	const { data } = useQuestionnaireDetailQuery({
		code: questionnaireCode,
	});
	const user = usePatientBasicInfoQuery({ id: patientId });
	const { identificationCode } = usePatientMeta(patientId);

	const exportQuestionnaire = async (
		resultIds: string[],
		withEvaluation: boolean = false,
	) => {
		if (resultIds.length < 1) {
			showNotificationAlert({
				content: t('component.questionnaireExportModal.lengthError'),
			});
			return;
		}
		if (data?.questionnaire?.id) {
			const parts: string[] = [];
			if (identificationCode) {
				parts.push(identificationCode);
				parts.push(moment().format('DD-MM-YYYY'));
			} else {
				if (user?.data?.user?.profile?.fullName) {
					parts.push(user?.data?.user?.profile?.fullName);
				}
				if (data?.questionnaire?.title) {
					parts.push(data?.questionnaire?.title);
				}
			}

			const fileName =
				sanitizeFileName({
					parts: parts,
				}) + '.xls';
			try {
				const response = await ApiClient.post(
					`/questionnaire/${data.questionnaire.id}/result/export/xls`,
					{ questionnaireResultIds: resultIds, withEvaluation },
					{ responseType: 'blob' },
				);
				// @ts-ignore
				const blob = new Blob([response.data], {
					type: 'xls;charset=utf-8',
				});
				// @ts-ignore
				const link = document.createElement('a');
				// @ts-ignore
				link.href = window.URL.createObjectURL(blob);
				link.download = fileName;
				link.click();
			} catch (e) {
				displayError(e);
			} finally {
				afterExport();
			}
		}
	};

	return {
		exportQuestionnaire,
	};
};

export { useExportQuestionnaire };
